/**
 * COMPARABLE-DATA-CONTRACT-1 – Kanonischer Vergleichswert-Datenvertrag
 *
 * Zweck:
 *   Jede Vergleichswertquelle wird über einen Adapter auf EIN gemeinsames
 *   Schema abgebildet, bevor eine Zahl überhaupt rechnen darf. Danach prüft
 *   eine fünfstufige Freigabelogik (Marktvergleich-Gate), ob die Datenlage
 *   für einen Marktvergleich trägt.
 *
 * Produktregeln (freigegeben am 2026-10-05):
 *   1. Angebotspreise (asking) bilden nur einen ANGEBOTSKORRIDOR –
 *      niemals einen "Marktwert".
 *   2. Für einen Marktwert braucht es TRANSAKTIONSDATEN (transaction).
 *   3. Demo-/Seed-Daten (legacy_seed) sind für Rechnung und Anzeige
 *      als Marktdatum gesperrt (Herkunftsnachweis fehlt).
 *
 * Quellenklassen (SOURCE_CATALOG):
 *   transaction_official  Amtliche Transaktion (Gutachterausschuss)
 *   land_value_official   Bodenrichtwert (BORIS)
 *   market_offer_licensed Lizenzierte Angebotsdaten (z. B. ImmoScout Market Data)
 *   portfolio_internal    Eigener Objektbestand (Clara360-Verwaltung)
 *   legacy_seed           Alt-/Demo-Daten (property_suggestions) – nicht freigegeben
 *   unknown               Unbekannte Quelle – nicht freigegeben
 *
 * @version 1.0.0
 * @contract COMPARABLE-DATA-CONTRACT-1
 */

export const CONTRACT_VERSION = 'COMPARABLE-DATA-CONTRACT-1';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Quellenkatalog
// ─────────────────────────────────────────────────────────────────────────────

export const SOURCE_CATALOG = {
  transaction_official: {
    id: 'transaction_official',
    label: 'Amtliche Transaktion (Gutachterausschuss)',
    license_class: 'official',
    trusted: true,
    default_price_type: 'transaction',
    note: 'Erfüllt die Herkunftsanforderung für Marktwert-Bewertungen.'
  },
  land_value_official: {
    id: 'land_value_official',
    label: 'Bodenrichtwert (BORIS)',
    license_class: 'official',
    trusted: true,
    default_price_type: 'land_value',
    note: 'Bodenwerte, keine Vergleichspreise. Ergänzend, nie allein.'
  },
  market_offer_licensed: {
    id: 'market_offer_licensed',
    label: 'Lizenzierte Angebotsdaten',
    license_class: 'licensed',
    trusted: true,
    default_price_type: 'asking',
    note: 'Nur Angebotskorridor – Angebotspreise sind keine Verkaufspreise.'
  },
  portfolio_internal: {
    id: 'portfolio_internal',
    label: 'Interner Objektbestand',
    license_class: 'internal',
    trusted: true,
    default_price_type: 'indication',
    note: 'Reale Miet- und Objekthistorie aus eigener Verwaltung.'
  },
  legacy_seed: {
    id: 'legacy_seed',
    label: 'Altbestand/Demo (nicht freigegeben)',
    license_class: 'none',
    trusted: false,
    default_price_type: 'indication',
    note: 'Entwicklungsdaten ohne Herkunftsnachweis – für Rechnung gesperrt.'
  },
  unknown: {
    id: 'unknown',
    label: 'Unbekannte Quelle',
    license_class: 'none',
    trusted: false,
    default_price_type: 'indication',
    note: 'Keine Quelle dokumentiert – für Rechnung gesperrt.'
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. Preistypen und Frische-Fenster
// ─────────────────────────────────────────────────────────────────────────────

export const PRICE_TYPE_RULES = {
  transaction: {
    id: 'transaction',
    label: 'Kaufpreis (Transaktion)',
    max_age_days: 1095, // 36 Monate
    market_capable: true,
    note: 'Erfüllt die Anforderung für Marktwert-Bewertungen.'
  },
  asking: {
    id: 'asking',
    label: 'Angebotspreis (Inserat)',
    max_age_days: 90,
    market_capable: false,
    note: 'Darf nur einen Angebotskorridor bilden, nie einen Marktwert.'
  },
  indication: {
    id: 'indication',
    label: 'Indikation (interne Schätzung)',
    max_age_days: 365,
    market_capable: false,
    note: 'Nur intern, nie als Marktdatum ausgewiesen.'
  },
  land_value: {
    id: 'land_value',
    label: 'Bodenrichtwert',
    max_age_days: 365,
    market_capable: false,
    note: 'Bodenwert – kein Vergleichspreis für Gebäude.'
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. Normalisierung – der Adapter-Kern
// ─────────────────────────────────────────────────────────────────────────────

function pick(raw, ...names) {
  for (const n of names) {
    if (raw[n] !== undefined && raw[n] !== null && raw[n] !== '') return raw[n];
  }
  return null;
}

function toNumber(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function toIsoDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Bildet einen rohen Datensatz auf das kanonische Schema ab.
 * Bereits kanonische Datensätze laufen unverändert durch.
 * Der Rohsatz bleibt als `source_row` erhalten (Audit-Trail).
 */
export function normalizeComparable(raw, sourceClass = 'unknown') {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.contract_version === CONTRACT_VERSION) return raw;

  const catalogEntry = SOURCE_CATALOG[sourceClass] || SOURCE_CATALOG.unknown;
  const price = toNumber(pick(raw, 'price', 'purchase_price', 'asking_price'));
  const livingSpace = toNumber(pick(raw, 'living_space', 'livingSpace', 'size', 'living_area'));
  const plotSize = toNumber(pick(raw, 'plot_size', 'plotSize', 'grundstuecksflaeche'));
  const buildYear = toNumber(pick(raw, 'build_year', 'buildYear', 'year_built', 'construction_year'));

  return {
    contract_version: CONTRACT_VERSION,
    external_id: pick(raw, 'external_id', 'id', 'source_id') || null,
    source_class: catalogEntry.id,
    source_name: catalogEntry.label,
    source_url: pick(raw, 'source_url', 'url', 'expose_url') || null,
    license_class: catalogEntry.license_class,
    price,
    price_type: pick(raw, 'price_type') || catalogEntry.default_price_type,
    currency: pick(raw, 'currency') || 'EUR',
    living_space: livingSpace,
    plot_size: plotSize,
    build_year: buildYear,
    property_type: pick(raw, 'property_type', 'propertyType', 'objektart') || null,
    address: pick(raw, 'address', 'street') || null,
    city: pick(raw, 'city') || null,
    zip_code: pick(raw, 'zip_code', 'zip', 'postal_code') || null,
    lat: toNumber(pick(raw, 'lat', 'latitude')),
    lng: toNumber(pick(raw, 'lng', 'lon', 'longitude')),
    transaction_date: toIsoDate(pick(raw, 'transaction_date', 'kaufdatum')),
    observed_at: toIsoDate(pick(raw, 'observed_at', 'created_at', 'updated_at')),
    source_row: raw
  };
}

/**
 * Adapter für den Altbestand (Tabelle `property_suggestions`).
 * Der einzige heute aktive Datenpfad – bewusst als Quelle `legacy_seed`
 * klassifiziert, bis echte Quellen über eigene Adapter eingespeist werden.
 */
export function adaptLegacySeedRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map(r => normalizeComparable(r, 'legacy_seed')).filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Herkunftssperre (Provenance) – aus der SEED-NEUTRALISIERUNG übernommen
// ─────────────────────────────────────────────────────────────────────────────

export function isPlaceholderSourceUrl(url) {
  if (!url || typeof url !== 'string') return true;
  const u = url.toLowerCase();
  const signals = [
    /\/expose\/12345/, /abc123/, /demo/, /test/, /example/, /sample/,
    /placeholder/, /musterstrasse/, /musterstadt/, /12345678/, /000000/
  ];
  return signals.some(rx => rx.test(u));
}

/**
 * Prüft je Datensatz, ob er als reales Marktdatum belegbar ist.
 * Arbeitet auf kanonischen UND rohen Datensätzen.
 */
export function assessComparableProvenance(comparables = []) {
  const list = Array.isArray(comparables) ? comparables : [];
  // Vorlagen-Muster: identische Preis-/Flächenkombination an mehreren Orten.
  const signatureCount = new Map();
  list.forEach(c => {
    if (!c || typeof c !== 'object') return;
    const price = toNumber(c.price);
    const size = toNumber(c.living_space ?? c.livingSpace ?? c.size);
    if (!(price > 0) || !(size > 0)) return;
    const key = price + '|' + size;
    signatureCount.set(key, (signatureCount.get(key) || 0) + 1);
  });
  return list.map(comp => {
    const reasons = [];
    if (!comp || typeof comp !== 'object') {
      return { comp, verified: false, reasons: ['ungueltiger Datensatz'] };
    }
    const price = toNumber(comp.price);
    const size = toNumber(comp.living_space ?? comp.livingSpace ?? comp.size);
    if (!(price > 0)) reasons.push('kein Preis hinterlegt');
    if (!(size > 0)) reasons.push('keine Flaeche hinterlegt');
    if (isPlaceholderSourceUrl(comp.source_url)) {
      reasons.push('Quelle nicht belegbar (Platzhalter- oder Fehlnachweis)');
    }
    if (comp.source_class && SOURCE_CATALOG[comp.source_class] && !SOURCE_CATALOG[comp.source_class].trusted) {
      reasons.push('Quelle nicht freigegeben: ' + SOURCE_CATALOG[comp.source_class].label);
    }
    if (price > 0 && size > 0 && (signatureCount.get(price + '|' + size) || 0) > 1) {
      reasons.push('Musterdatensatz: identische Preis-/Flaechenkombination an mehreren Standorten');
    }
    return { comp, verified: reasons.length === 0, reasons };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Ähnlichkeit und Lage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ähnlichkeitsscore 0–100 zwischen Objekt und Vergleichsobjekt.
 * Übernommene Logik aus ValuationService.calculateSimilarityScore –
 * akzeptiert kanonische und rohe Feldnamen.
 */
export function similarityScore(subject, comparable) {
  let score = 100;
  const propertySize = subject.total_area || subject.total_square_meters ||
    subject.rentable_area || subject.size || subject.living_space || 100;
  const compSize = comparable.living_space ?? comparable.livingSpace ?? comparable.size ?? 100;
  const sizeDiff = Math.abs(propertySize - compSize) / propertySize;
  score -= Math.min(sizeDiff * 100, 20);
  const propertyYear = subject.year_built || subject.construction_year || subject.build_year;
  const compYear = comparable.build_year ?? comparable.buildYear ?? comparable.year_built;
  if (propertyYear && compYear) {
    const yearDiff = Math.abs(propertyYear - compYear);
    score -= Math.min(yearDiff / 5, 20);
  }
  const subjType = subject.property_type || subject.propertyType;
  const compType = comparable.property_type ?? comparable.propertyType;
  if (subjType && compType && subjType !== compType) {
    score -= 20;
  }
  return Math.max(Math.round(score), 0);
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Lageprüfung: gleiche Stadt ODER Luftlinie <= radius_km.
 */
export function isLocalMatch(subject, comparable, radiusKm = 5) {
  const subjCity = subject.city || subject.address?.split(',').pop()?.trim();
  const compCity = comparable.city ?? comparable.address?.split(',').pop()?.trim();
  if (subjCity && compCity && subjCity === compCity) return true;
  const sLat = subject.lat ?? subject.latitude;
  const sLng = subject.lng ?? subject.lon ?? subject.longitude;
  const cLat = comparable.lat ?? comparable.latitude;
  const cLng = comparable.lng ?? comparable.lon ?? comparable.longitude;
  if ([sLat, sLng, cLat, cLng].every(v => typeof v === 'number' && Number.isFinite(v))) {
    return haversineKm(sLat, sLng, cLat, cLng) <= radiusKm;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Frische
// ─────────────────────────────────────────────────────────────────────────────

export function freshnessStatus(comparable, now = Date.now()) {
  const rules = PRICE_TYPE_RULES[comparable.price_type] || PRICE_TYPE_RULES.indication;
  const ref = comparable.observed_at || comparable.transaction_date;
  if (!ref) {
    return { ok: false, ageDays: null, maxAgeDays: rules.max_age_days, label: 'kein Beobachtungsdatum' };
  }
  const ageDays = Math.round((now - new Date(ref).getTime()) / 86400000);
  return {
    ok: ageDays >= 0 && ageDays <= rules.max_age_days,
    ageDays,
    maxAgeDays: rules.max_age_days,
    label: ageDays + ' Tage alt (max. ' + rules.max_age_days + ')'
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. Freigabelogik – das Marktvergleich-Gate
// ─────────────────────────────────────────────────────────────────────────────

export const MARKET_READINESS_THRESHOLDS = {
  min_verified_comparables: 5,
  min_per_gate: 3,
  min_similarity_score: 60,
  location_radius_km: 5
};

/**
 * Prüft die fünf Freigabekriterien für den Marktvergleich:
 *   Quelle · Lage · Frische · Ähnlichkeit · Mindestmenge
 *
 * Gibt eine Checkliste für die Oberfläche zurück. Das Gate bleibt solange
 * geschlossen, wie auch nur ein Kriterium offen ist.
 */
export function assessMarketReadiness(subject, comparables = [], options = {}) {
  const th = { ...MARKET_READINESS_THRESHOLDS, ...options };
  const provenance = assessComparableProvenance(comparables);
  const verified = provenance.filter(p => p.verified).map(p => p.comp);

  const local = verified.filter(c => isLocalMatch(subject, c, th.location_radius_km));
  const fresh = verified.filter(c => freshnessStatus(c).ok);
  const similar = verified.filter(c => similarityScore(subject, c) >= th.min_similarity_score);
  const trusted = verified.filter(c => {
    const entry = SOURCE_CATALOG[c.source_class];
    return entry && entry.trusted && entry.license_class !== 'none';
  });

  const checklist = [
    {
      id: 'source',
      label: 'Belegbare Quelle',
      ok: trusted.length >= th.min_per_gate,
      detail: trusted.length + '/' + th.min_per_gate + ' mit lizenzierter Herkunft',
      hint: 'Gutachterausschuss, lizenzierte Angebotsdaten oder interner Bestand'
    },
    {
      id: 'location',
      label: 'Lagebezug',
      ok: local.length >= th.min_per_gate,
      detail: local.length + '/' + th.min_per_gate + ' im selben Markt (≤ ' + th.location_radius_km + ' km)',
      hint: 'Vergleichswerte aus derselben Lage'
    },
    {
      id: 'freshness',
      label: 'Aktualität',
      ok: fresh.length >= th.min_per_gate,
      detail: fresh.length + '/' + th.min_per_gate + ' im Frischefenster',
      hint: 'Transaktionen max. 36 Monate, Angebote max. 90 Tage'
    },
    {
      id: 'similarity',
      label: 'Objektähnlichkeit',
      ok: similar.length >= th.min_per_gate,
      detail: similar.length + '/' + th.min_per_gate + ' mit Ähnlichkeit ≥ ' + th.min_similarity_score,
      hint: 'Größe, Baujahr und Objektart im Vergleich'
    },
    {
      id: 'min_count',
      label: 'Mindestmenge',
      ok: verified.length >= th.min_verified_comparables,
      detail: verified.length + '/' + th.min_verified_comparables + ' belastbare Datensätze',
      hint: 'Erst ab ausreichender Menge ist ein Korridor belastbar'
    }
  ];

  const ready = checklist.every(g => g.ok);
  const marketCapable = verified.some(c => (PRICE_TYPE_RULES[c.price_type] || {}).market_capable);

  return {
    contract_version: CONTRACT_VERSION,
    ready,
    checklist,
    missing: checklist.filter(g => !g.ok).map(g => g.label),
    counts: {
      total: comparables.length,
      verified: verified.length,
      rejected: provenance.length - verified.length
    },
    // Produktregel: Angebotsdaten bilden nur einen Angebotskorridor
    result_label: ready
      ? (marketCapable ? 'Marktwertbasis (Transaktionen)' : 'Angebotskorridor (kein Marktwert)')
      : 'Marktvergleich nicht freigegeben',
    summary: ready
      ? 'Alle Freigabekriterien erfüllt – ' + (marketCapable ? 'Transaktionsdaten tragen einen Marktwert.' : 'nur Angebotskorridor, kein Marktwert.')
      : 'Offen: ' + checklist.filter(g => !g.ok).map(g => g.label).join(', ')
  };
}
